import React, { useState, useEffect, useCallback } from 'react';
import { getFirestore, collection, getDocs, doc, updateDoc, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import './AdminUserManagement.css';

const AdminUserManagement = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [activeUsers, setActiveUsers] = useState([]);
  const [suspendedUsers, setSuspendedUsers] = useState([]);

  // ✅ ADD MIGRATION STATE
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationResults, setMigrationResults] = useState(null);
  
  const db = getFirestore();
  const auth = getAuth();
  const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

  const fetchUsers = useCallback(async () => {
    try {
      const usersCollection = collection(db, 'users');
      const snapshot = await getDocs(usersCollection);
      const users = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setPendingUsers(users.filter(user => user.status === 'pending'));
      setActiveUsers(users.filter(user => user.status === 'active'));
      setSuspendedUsers(users.filter(user => user.status === 'suspended'));
      setAllUsers(users);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ✅ ADD MIGRATION FUNCTIONS
  const runRegionMigration = async () => {
    setMigrationLoading(true);
    setMigrationResults(null);
    
    try {
      console.log('🔄 Starting region migration...');
      
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      
      let migratedCount = 0;
      let skippedCount = 0;
      let errorCount = 0;
      const errors = [];

      for (const userDoc of usersSnapshot.docs) {
        try {
          const userData = userDoc.data();
          const userId = userDoc.id;
          
          // Skip if already has allowedRegions
          if (userData.allowedRegions) {
            console.log(`✅ User ${userData.email} already has region fields, skipping`);
            skippedCount++;
            continue;
          }
          
          // Determine user's default region based on existing data
          const defaultRegion = userData.lastRegion || 'vquest';
          
          const updateData = {
            allowedRegions: [defaultRegion],
            defaultRegion: defaultRegion,
            regionAccess: {
              [defaultRegion]: {
                assignedAt: new Date().toISOString(),
                assignedBy: auth.currentUser?.email || 'admin-migration',
                status: 'active'
              }
            },
            // Update companyProfile if it exists
            companyProfile: {
              ...userData.companyProfile,
              operatingRegions: [defaultRegion],
              primaryRegion: defaultRegion
            }
          };
          
          await setDoc(doc(db, 'users', userId), updateData, { merge: true });
          console.log(`✅ Migrated user: ${userData.email} with region: ${defaultRegion}`);
          migratedCount++;
          
        } catch (error) {
          console.error(`❌ Error migrating user ${userDoc.id}:`, error);
          errors.push(`${userDoc.id}: ${error.message}`);
          errorCount++;
        }
      }
      
      const results = {
        migrated: migratedCount,
        skipped: skippedCount,
        errors: errorCount,
        errorDetails: errors
      };
      
      setMigrationResults(results);
      console.log('🎉 Migration completed!', results);
      
      // Refresh user list to show updated data
      await fetchUsers();
      
    } catch (error) {
      console.error('❌ Migration failed:', error);
      setMigrationResults({
        migrated: 0,
        skipped: 0,
        errors: 1,
        errorDetails: [error.message]
      });
    } finally {
      setMigrationLoading(false);
    }
  };

  const checkMigrationStatus = () => {
    const usersWithRegions = allUsers.filter(user => user.allowedRegions).length;
    const usersWithoutRegions = allUsers.filter(user => !user.allowedRegions).length;
    
    return {
      total: allUsers.length,
      migrated: usersWithRegions,
      needsMigration: usersWithoutRegions
    };
  };

  // ✅ ADD MIGRATION STATUS COMPONENT
  const renderMigrationSection = () => {
    const status = checkMigrationStatus();
    
    return (
      <div className="migration-section">
        <h3>🌏 Region Migration</h3>
        <div className="migration-status">
          <p><strong>Total Users:</strong> {status.total}</p>
          <p><strong>Users with Region Data:</strong> {status.migrated}</p>
          <p><strong>Users Needing Migration:</strong> {status.needsMigration}</p>
        </div>
        
        {status.needsMigration > 0 ? (
          <button 
            onClick={runRegionMigration}
            disabled={migrationLoading}
            className="migration-btn"
          >
            {migrationLoading ? 'Migrating Users...' : `Add Region Fields to ${status.needsMigration} Users`}
          </button>
        ) : (
          <div className="migration-complete">
            ✅ All users have region data!
          </div>
        )}
        
        {migrationResults && (
          <div className="migration-results">
            <h4>Migration Results:</h4>
            <ul>
              <li>✅ Migrated: {migrationResults.migrated} users</li>
              <li>⏩ Skipped: {migrationResults.skipped} users (already had region data)</li>
              <li>❌ Errors: {migrationResults.errors} users</li>
            </ul>
            
            {migrationResults.errorDetails.length > 0 && (
              <details>
                <summary>Error Details</summary>
                <ul>
                  {migrationResults.errorDetails.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    );
  };

  // ✅ UPDATED: Enhanced approval function with better email
  const approveUser = async (userId, userEmail, userRole) => {
    try {
      console.log(`📝 Approving user: ${userEmail} (${userRole})`);
      
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        status: 'active',
        approvedBy: auth.currentUser?.uid,
        approvedAt: new Date().toISOString()
      });

      console.log(`✅ User status updated in Firestore`);

      // Until now every authenticated call from this user was 403'd (status was
      // 'pending'), so they could not redeem a company invite themselves. Now
      // that they can log in, join them to whichever company invited them.
      // Best-effort: approval must not fail because this did.
      try {
        const token = await auth.currentUser?.getIdToken();
        await fetch(`${API_BASE}/connections/auto-join`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-user-email': auth.currentUser?.email || '',
            'x-user-role': 'admin',
          },
          body: JSON.stringify({ email: userEmail }),
        });
      } catch (joinErr) {
        console.warn('Company auto-join failed (user still approved):', joinErr.message);
      }

      // Send approval email with login link
      await sendApprovalEmail(userEmail, userRole, 'approved');
      
      await fetchUsers();
      
      alert(`✅ User ${userEmail} has been approved and notified via email!`);
    } catch (error) {
      console.error('❌ Error approving user:', error);
      alert('Error approving user. Please try again.');
    }
  };

  const rejectUser = async (userId, userEmail, userRole, reason = 'Application did not meet requirements') => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        status: 'rejected',
        rejectedBy: auth.currentUser?.uid,
        rejectedAt: new Date().toISOString(),
        rejectionReason: reason
      });

      await sendApprovalEmail(userEmail, userRole, 'rejected', reason);
      await fetchUsers();
      
      alert(`User ${userEmail} has been rejected.`);
    } catch (error) {
      console.error('Error rejecting user:', error);
      alert('Error rejecting user. Please try again.');
    }
  };

  const suspendUser = async (userId, userEmail) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        status: 'suspended',
        suspendedBy: auth.currentUser?.uid,
        suspendedAt: new Date().toISOString()
      });

      await fetchUsers();
      alert(`User ${userEmail} has been suspended.`);
    } catch (error) {
      console.error('Error suspending user:', error);
    }
  };

  // ✅ Send approval/rejection email via backend Nodemailer
  const sendApprovalEmail = async (userEmail, userRole, status, reason = '') => {
    try {
      console.log(`📧 Sending ${status} email to: ${userEmail}`);
      
      // Get the Firebase auth token for the authenticated admin
      const idToken = await auth.currentUser.getIdToken();
      
      const res = await fetch(`${API_BASE}/email/send-user-approval`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          userEmail,
          userName: userEmail.split('@')[0],
          userRole,
          status,
          reason,
          adminName: auth.currentUser?.email || 'Admin Team'
        })
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `HTTP ${res.status}`);
      }

      console.log(`✅ ${status} email sent successfully to ${userEmail}`);
      
    } catch (error) {
      console.error('❌ Error sending approval email:', error);
      
      alert(`Warning: User was ${status} successfully, but email notification failed. 
      
Error: ${error.message}
      
Please notify ${userEmail} manually that their account has been ${status}.
      
Login URL: https://ops-console.nezo-app.com/login`);
      
      throw error;
    }
  };

  const renderUserCard = (user) => (
    <div key={user.id} className="user-card">
      <div className="user-info">
        <h3>{user.email}</h3>
        <div className="user-details">
          <span className={`role-badge ${user.role}`}>{user.role.toUpperCase()}</span>
          <span className={`status-badge ${user.status}`}>{user.status.toUpperCase()}</span>
          {/* ✅ ADD REGION INFO DISPLAY */}
          {user.allowedRegions && (
            <span className="region-info">
              🌏 Regions: {user.allowedRegions.join(', ')}
            </span>
          )}
        </div>
        
        {user.role === 'supplier' && (
          <div className="supplier-details">
            <p><strong>Company:</strong> {user.companyName || user.companyProfile?.companyName || 'N/A'}</p>
            <p><strong>Contact:</strong> {user.contactPerson || user.companyProfile?.primaryContact || 'N/A'}</p>
            <p><strong>Phone:</strong> {user.phone || user.companyProfile?.phoneNumber || 'N/A'}</p>
          </div>
        )}
        
        <div className="user-meta">
          <p><strong>Created:</strong> {new Date(user.createdAt).toLocaleString()}</p>
          <p><strong>User ID:</strong> {user.id}</p>
          {/* ✅ ADD REGION STATUS */}
          <p><strong>Region Data:</strong> {user.allowedRegions ? '✅ Configured' : '❌ Missing'}</p>
          {user.approvedAt && (
            <p><strong>Approved:</strong> {new Date(user.approvedAt).toLocaleString()}</p>
          )}
        </div>
      </div>

      <div className="user-actions">
        {user.status === 'pending' && (
          <>
            <button 
              className="approve-btn"
              onClick={() => approveUser(user.id, user.email, user.role)}
              title={`Approve ${user.email} and send login email`}
            >
              ✅ Approve & Notify
            </button>
            <button 
              className="reject-btn"
              onClick={() => {
                const reason = prompt('Rejection reason (optional):') || 'Application did not meet requirements';
                rejectUser(user.id, user.email, user.role, reason);
              }}
            >
              ❌ Reject
            </button>
          </>
        )}
        
        {user.status === 'active' && (
          <button 
            className="suspend-btn"
            onClick={() => suspendUser(user.id, user.email)}
          >
            🚫 Suspend
          </button>
        )}
      </div>
    </div>
  );

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="admin-user-management">
      <h1>User Management</h1>
      
      {/* ✅ ADD MIGRATION SECTION */}
      {renderMigrationSection()}

      {/* ✅ EMAIL STATUS DISPLAY */}
      <div className="email-status" style={{
        padding: '1rem',
        backgroundColor: '#e8f5e8',
        borderRadius: '8px',
        marginBottom: '1rem',
        border: '1px solid #4CAF50'
      }}>
        <h4>📧 Email Notifications</h4>
        <p>✅ Approval/rejection emails sent via backend Nodemailer</p>
      </div>
      
      <div className="tabs">
        <button 
          className={activeTab === 'pending' ? 'active' : ''}
          onClick={() => setActiveTab('pending')}
        >
          Pending Approval ({pendingUsers.length})
        </button>
        <button 
          className={activeTab === 'active' ? 'active' : ''}
          onClick={() => setActiveTab('active')}
        >
          Active Users ({activeUsers.length})
        </button>
        <button 
          className={activeTab === 'suspended' ? 'active' : ''}
          onClick={() => setActiveTab('suspended')}
        >
          Suspended Users ({suspendedUsers.length})
        </button>
        <button 
          className={activeTab === 'all' ? 'active' : ''}
          onClick={() => setActiveTab('all')}
        >
          All Users ({allUsers.length})
        </button>
      </div>

      <div className="user-management-list">
        {activeTab === 'pending' ? (
          pendingUsers.length > 0 ? (
            pendingUsers.map(renderUserCard)
          ) : (
            <div className="no-users">No pending users</div>
          )
        ) : activeTab === 'active' ? (
          activeUsers.length > 0 ? (
            activeUsers.map(renderUserCard)
          ) : (
            <div className="no-users">No active users</div>
          )
        ) : activeTab === 'suspended' ? (
          suspendedUsers.length > 0 ? (
            suspendedUsers.map(renderUserCard)
          ) : (
            <div className="no-users">No suspended users</div>
          )
        ) : (
          allUsers.map(renderUserCard)
        )}
      </div>
    </div>
  );
};

export default AdminUserManagement;